/**
 * Highlight categories used by the NowPlayingHeader "Show:" pill row.
 *
 * Each category bundles:
 *   - a stable id (the filter discriminant in BrandedPlaylistFilter)
 *   - a display label (the pill text, e.g. "ACES")
 *   - a predicate matching it against a clip's computed tag badges
 *
 * Centralized here so the header pills, count badges, and the visibleClips
 * resolver in BrandedEmbeddablePlaylist all share one source of truth —
 * adding a new category (e.g. CLUTCHES, HEADSHOTS) is one entry in
 * CATEGORY_DEFS, not three coordinated edits.
 *
 * The category id type lives in State.ts (`BrandedPlaylistCategory`)
 * because Recoil atoms consume it; the definitions live here so the
 * predicate machinery doesn't bloat State.ts with rendering concerns.
 */
import { getTagBadges } from "MVVM/Utilities/ClipTags";

import type { PlaylistClip } from "../../@types";
import type { BrandedPlaylistCategory } from "./State";

export interface CategoryDef {
  id: BrandedPlaylistCategory;
  label: string;
  matches: (clip: PlaylistClip) => boolean;
}

/**
 * Resolve the canonical badge set for a clip using its REAL partner-api
 * metadata. Previously this routed through `buildMockTClipTags`, which
 * assigned a random badge combo per clip ID hash — that made the filter
 * predicates unrelated to the actual clip content (clicking ACES would
 * return mock-aces, not real aces). Mock data is only used to enrich
 * the playlist row's display tags; filter logic must reflect ground
 * truth from the clip itself.
 *
 * `clip.metadata` shape is `{key, value, category?}[]` matching the
 * `TClipTag` contract `getTagBadges` expects. The category field on
 * each metadata entry is dropped by mapping down to `{key, value}` so
 * ClipTags' shape contract is respected.
 */
const realBadges = (clip: PlaylistClip) => {
  // PlaylistClip.metadata is typed as Record<string, unknown>[] at the
  // top-level @types layer (the partner-api wire shape is loose), but
  // at runtime each entry has {key, value} string fields. Coerce
  // through here so getTagBadges sees a typed TClipTag[].
  const tags = ((clip.metadata ?? []) as { key: string; value: string }[])
    .filter((m) => typeof m?.key === "string" && typeof m?.value === "string")
    .map((m) => ({ key: m.key, value: m.value }));
  return getTagBadges(tags);
};

/**
 * Predicate helper — true when any of the clip's computed badges has a
 * label matching `target` (case-insensitive).
 */
const hasBadge = (clip: PlaylistClip, target: string): boolean => {
  const badges = realBadges(clip);
  return badges.some((b) => b.label.toUpperCase() === target.toUpperCase());
};

/** Predicate helper — true when any badge label matches one of `targets`. */
const hasAnyBadge = (clip: PlaylistClip, targets: string[]): boolean => {
  const badges = realBadges(clip);
  const set = new Set(targets.map((t) => t.toUpperCase()));
  return badges.some((b) => set.has(b.label.toUpperCase()));
};

/**
 * Ordered from most prestigious → least so the header can take the
 * first N defs with a non-zero count when not all categories have
 * matches in the current playlist. Aces and Clutches are the headline
 * categories; 4K / 3K are fallbacks that keep the pill row populated
 * for "regular" playlists where nobody happened to hit an ace or
 * clutch a round.
 */
export const CATEGORY_DEFS: CategoryDef[] = [
  {
    id: "aces",
    label: "Aces",
    matches: (clip) => hasBadge(clip, "ACE"),
  },
  {
    id: "clutches",
    label: "Clutches",
    matches: (clip) => hasAnyBadge(clip, ["1V2", "1V3", "1V4", "1V5"]),
  },
  {
    id: "fourk",
    label: "4K",
    matches: (clip) => hasBadge(clip, "4 KILLS"),
  },
  {
    id: "threek",
    label: "3K",
    matches: (clip) => hasBadge(clip, "3 KILLS"),
  },
];

/**
 * Filter-resolver entry point. Returns true when the clip satisfies the
 * given category's predicate; the visibleClips resolver short-circuits
 * the "no filter" case before calling this so we only handle the
 * filtered branch.
 */
export const categoryMatches = (
  category: BrandedPlaylistCategory,
  clip: PlaylistClip,
): boolean => {
  const def = CATEGORY_DEFS.find((d) => d.id === category);
  return def ? def.matches(clip) : false;
};

/**
 * Count clips per category. Used by NowPlayingHeader to render the inline
 * `N` next to each pill — surfaces the cost of activating a filter before
 * the user clicks.
 */
export const countByCategory = (
  clips: PlaylistClip[],
  category: BrandedPlaylistCategory,
): number => clips.filter((c) => categoryMatches(category, c)).length;
